import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";
import { HrSummaryPanel } from "../../../../design-system/hr-order";
import { SummaryPriceCard, BookingCTA } from "../../../../design-system/review";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { formatUsdDisplayInteger } from "../../../../utils/ceilUsd";
import { mapFlightProposalToVM } from "../../../../review-vm/mapFlightToVM";
import "./PriceSummaryCard.scss";

const MAX_BREAKDOWN_LINES = 4;
const CTA_LABEL = "המשך לתשלום מאובטח";
const SECURE_NOTE = "התשלום מאובטח ומוצפן";

/**
 * Flight proposal sidebar / compact summary — composes design-system SummaryPriceCard.
 * Adapts API props + context (ilsToUsd) without changing fetch or routes.
 */
export default function PriceSummaryCard({
  variant = "sidebar",
  total,
  currency,
  priceLines,
  statusLabel,
  profilePath,
  onBackOrders,
}) {
  const { ilsToUsd } = useSelectedResult();
  const cur = currency ? String(currency).trim() : "";
  const totalNum = total != null ? Number(total) : null;
  const showTotal = Number.isFinite(totalNum);

  const vm = useMemo(
    () =>
      mapFlightProposalToVM({
        proposal: { total_price: total },
        offer: {},
        statusLabel,
        priceSidebar: { lines: priceLines || [] },
      }),
    [total, statusLabel, priceLines]
  );

  const displayLines = useMemo(
    () => (priceLines || []).slice(0, MAX_BREAKDOWN_LINES),
    [priceLines]
  );

  const isIls = !cur || cur.toUpperCase() === "ILS" || cur === "₪";

  const primaryAmountDisplay = showTotal
    ? isIls
      ? `${Math.round(totalNum).toLocaleString("he-IL")} ₪`
      : `${Math.round(totalNum).toLocaleString("he-IL")} ${cur}`.trim()
    : null;

  const secondaryAmountDisplay =
    showTotal && isIls && typeof ilsToUsd === "function"
      ? `(${formatUsdDisplayInteger(ilsToUsd(totalNum))}$)`
      : null;

  const breakdownRows = displayLines.map((row) => ({
    key: row.key,
    label: row.label,
    valueText: row.amount != null ? `${row.amount} ${cur}`.trim() : "—",
  }));

  const statusFromVm = vm.badges[0]?.label;

  const footer =
    variant === "sidebar" ? (
      <>
        <Link to={profilePath} className="hr-order-summary-panel__cta-wrap">
          <BookingCTA variant="primary">{CTA_LABEL}</BookingCTA>
        </Link>
        <div className="hr-fp-summary-card__secure" aria-live="polite">
          <LockOutlined className="hr-fp-summary-card__lock" aria-hidden />
          <span>{SECURE_NOTE}</span>
        </div>
        <BookingCTA variant="ghost" onClick={onBackOrders}>
          חזרה להזמנות
        </BookingCTA>
      </>
    ) : null;

  return (
    <HrSummaryPanel
      variant={variant}
      className="hr-order-summary-panel--surface"
      aria-label="סיכום הצעה"
    >
      <SummaryPriceCard
        title={vm.title}
        statusLabel={statusFromVm}
        totalLabel='סה״כ לתשלום'
        primaryAmountDisplay={primaryAmountDisplay}
        secondaryAmountDisplay={secondaryAmountDisplay}
        breakdownRows={breakdownRows}
        footer={footer}
        density={variant === "compact" ? "compact" : "default"}
      />
    </HrSummaryPanel>
  );
}
